import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { RadioButtons } from "@grandvisionhq/forms";
import { H2, H3 } from "@grandvisionhq/iris-ui";
import { useIntl } from "@grandvisionhq/state";
import { useNearestStore } from "../../../../../../modules/ServiceStore/hooks/modules/ServiceStore/hooks/use-nearest-store.js";
import { CustomerType } from "../../../../../../types.js";
import { useUserStateConfig } from "../../../../../hooks/LoginExpressCheckout/hooks/useUserStateConfig.js";
import { AddressSummary } from "../../../Summary/LoginExpressCheckout/modules/UserDetails/Summary/AddressSummary.js";
import { CompanySummary } from "../../../Summary/LoginExpressCheckout/modules/UserDetails/Summary/CompanySummary.js";
import { ContactDetailsSummary } from "../../../Summary/LoginExpressCheckout/modules/UserDetails/Summary/ContactDetailsSummary.js";
import { InvoiceSummary } from "../../../Summary/LoginExpressCheckout/modules/UserDetails/Summary/InvoiceSummary.js";
import { ServiceStore } from "../../../Summary/ServiceStore/LoginExpressCheckout/modules/UserDetails/Summary/ServiceStore/ServiceStore.js";
import { useLoggedInCheckout } from "../../../../../context/LoginExpressCheckout/context/context.js";
import { EditableLoyaltyPointsSummary } from "../../../../../../StepOverview/EditableSummaries/StepOverview/EditableSummaries/EditableLoyaltyPointsSummary.js";
import { useCheckoutSettings } from "../../../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const UserDetails = () => {
  const {
    billingAddress,
    contactDetails,
    globalStoreId,
    areUserDetailsLoading,
    error,
    isUserLoggedIn
  } = useUserStateConfig();
  const {
    userDetailsState,
    setUserDetailsState,
    isCompanyOrder,
    isPickupOrder,
    shippingState,
    setShippingState,
    setIsCompany,
    isOrderDetailsValid,
    isAddressesEqual,
    isFailedToFetchUserData,
    setIsFailedToFetchUserData,
    setIsUserLoggedIn
  } = useLoggedInCheckout();
  const { getLabel } = useIntl();
  const { features, serviceStore } = useCheckoutSettings();
  const { cart } = Context.useCart();
  const cartServiceStoreId = React.useMemo(
    () => cart?.serviceStore?.globalStoreId,
    [cart?.serviceStore?.globalStoreId]
  );
  const isServiceStoreValid = React.useMemo(
    () => !features?.isServiceStoreEnabled || shippingState.serviceStore || serviceStore?.isOptional,
    [features?.isServiceStoreEnabled, serviceStore?.isOptional, shippingState.serviceStore]
  );
  const labels = React.useMemo(
    () => ({
      customerType: {
        private: getLabel("checkout.customerType.private"),
        company: getLabel("checkout.customerType.company")
      },
      serviceStore: {
        title: `${getLabel("checkout.serviceStore.title")} ${features?.isServiceStoreEnabled && serviceStore?.isOptional ? `(${getLabel("checkout.form.field.optional").toLowerCase()})` : ""}`
      }
    }),
    [features?.isServiceStoreEnabled, getLabel, serviceStore?.isOptional]
  );
  const mergeUserContactDetailsState = (localState, apiDataState) => {
    return {
      ...localState,
      ...Object.fromEntries(
        Object.entries(apiDataState).filter(([key, value]) => !localState[key] && value)
      )
    };
  };
  const syncUserDetailsState = React.useCallback(() => {
    const isLocalBillingAddressEmpty = !userDetailsState?.billingAddress || Object.values(userDetailsState.billingAddress).every((value) => value == null || value === "");
    const isContactDetailsEmpty = !userDetailsState?.contactDetails || Object.values(userDetailsState.contactDetails).every((value) => value == null || value === "");
    setUserDetailsState({
      ...userDetailsState,
      ...isLocalBillingAddressEmpty ? { billingAddress } : {},
      ...isContactDetailsEmpty ? { contactDetails } : userDetailsState?.contactDetails && contactDetails ? {
        contactDetails: mergeUserContactDetailsState(
          userDetailsState?.contactDetails,
          contactDetails
        )
      } : {},
      ...!userDetailsState.deliveryAddress ? { deliveryAddress: void 0 } : {},
      ...!userDetailsState?.invoice ? { invoice: void 0 } : {},
      ...!userDetailsState?.company ? { company: void 0 } : {}
    });
    if (!userDetailsState.deliveryAddress) {
      setShippingState({ ...shippingState, isAddressesEqual: true });
    }
  }, [
    billingAddress,
    contactDetails,
    setShippingState,
    setUserDetailsState,
    shippingState,
    userDetailsState
  ]);
  const saveFormDetails = React.useCallback(
    (formData) => {
      if (!formData) return;
      setUserDetailsState({
        ...userDetailsState,
        ...formData
      });
    },
    [setUserDetailsState, userDetailsState]
  );
  const handleAddressEqual = React.useCallback(
    (value) => {
      setShippingState({
        ...shippingState,
        isAddressesEqual: value
      });
    },
    [setShippingState, shippingState]
  );
  const handleFocusForError = () => {
    if (typeof window !== "undefined") {
      const topErrorField = window.document.querySelector(
        'div[role="alert"].alert-m-default'
      );
      if (topErrorField) {
        topErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        topErrorField?.focus();
      }
    }
  };
  const nearestStoreQuery = React.useMemo(() => {
    if (billingAddress) {
      const postalCode = billingAddress?.postalCode || "";
      const streetName = billingAddress?.streetName || "";
      const streetNumber = billingAddress?.streetNumber || "";
      const city = billingAddress?.city || "";
      return `${postalCode} ${streetName} ${streetNumber} ${city}`.replace(/\s{2,}/g, " ").trim();
    }
    return "";
  }, [billingAddress]);
  const [storeId, isLoadingNearestStore] = useNearestStore(
    nearestStoreQuery,
    !globalStoreId && !cartServiceStoreId && !!serviceStore?.preselectFromShippingData
  );
  React.useEffect(() => {
    if (!areUserDetailsLoading) {
      syncUserDetailsState();
    }
    if (!isOrderDetailsValid) {
      handleFocusForError();
    }
  }, [areUserDetailsLoading, isOrderDetailsValid]);
  const handleUserDataFetchError = React.useCallback(() => {
    if (isFailedToFetchUserData) return;
    if (!isFailedToFetchUserData) {
      setIsFailedToFetchUserData(true);
    }
  }, [isFailedToFetchUserData, setIsFailedToFetchUserData]);
  const isServiceStoreFeatureEnabled = React.useMemo(
    () => features?.isServiceStoreEnabled && !isPickupOrder && !isLoadingNearestStore,
    [features?.isServiceStoreEnabled, isPickupOrder, isLoadingNearestStore]
  );
  React.useEffect(() => {
    if (error) {
      handleUserDataFetchError();
    }
  }, [error]);
  React.useEffect(() => {
    if (isUserLoggedIn) {
      setIsUserLoggedIn(true);
    }
  }, [isUserLoggedIn]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(H2, { variant: "heading-small" }, getLabel("checkout.instant.userDetails.heading")), /* @__PURE__ */ React.createElement(H3, { variant: "text-body" }, getLabel("checkout.instant.userDetails.subheading")), Boolean(features?.isCompanyEnabled) && /* @__PURE__ */ React.createElement(
    RadioButtons,
    {
      scope: "orderType",
      alignment: "horizontal",
      onChange: (radioState) => setIsCompany(radioState.value === CustomerType.Company),
      initialValue: isCompanyOrder ? CustomerType.Company : CustomerType.Private,
      items: [
        {
          value: CustomerType.Private,
          label: labels.customerType.private
        },
        {
          value: CustomerType.Company,
          label: labels.customerType.company
        }
      ]
    }
  ), /* @__PURE__ */ React.createElement(
    AddressSummary,
    {
      address: userDetailsState?.billingAddress,
      deliveryAddress: userDetailsState?.deliveryAddress,
      isPickupOrder,
      onSubmit: saveFormDetails,
      isOrderDetailsValid,
      isAddressesEqual,
      handleAddressEqual
    }
  ), /* @__PURE__ */ React.createElement(
    ContactDetailsSummary,
    {
      onSubmit: saveFormDetails,
      contactDetails: userDetailsState?.contactDetails,
      isOrderDetailsValid,
      isFailedToFetchUserData
    }
  ), !isCompanyOrder ? /* @__PURE__ */ React.createElement(
    InvoiceSummary,
    {
      onSubmit: saveFormDetails,
      invoiceDetails: userDetailsState?.invoice,
      isOrderDetailsValid
    }
  ) : /* @__PURE__ */ React.createElement(
    CompanySummary,
    {
      onSubmit: saveFormDetails,
      companyDetails: userDetailsState?.company,
      isOrderDetailsValid
    }
  ), /* @__PURE__ */ React.createElement(EditableLoyaltyPointsSummary, null), isServiceStoreFeatureEnabled && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ServiceStore,
    {
      globalStoreId: globalStoreId || storeId,
      title: labels.serviceStore.title,
      isServiceStoreValid: Boolean(isServiceStoreValid),
      isOrderDetailsValid
    }
  )));
};
export {
  UserDetails
};
//# sourceMappingURL=UserDetails.js.map
